// variant of https://github.com/VividSeats/vivid-ui-kit/blob/main/src/components/production-listing-row/component.tsx
import React, { forwardRef, HTMLAttributes } from 'react'

import { ArrowForwardIcon, Box, Column, Pill, Row, Text } from '@vividseats/vivid-ui-kit'
import classnames from 'classnames'

import MatchupPill from '@/optimizely/ncaa-regions-feature/components/matchup-pill'

import styles from './styles.module.scss'
import { useNCAAProperties } from './utils'

interface ProductionListingRowType extends HTMLAttributes<HTMLAnchorElement> {
    date?: string
    day?: string
    time?: string
    title: string
    href?: string
    productionId?: number
    venueName: string
    location: string
    isDesktop?: boolean
}

const NcaaBasketballVariantProductionListingRow: React.ForwardRefRenderFunction<
    React.MutableRefObject<unknown>,
    ProductionListingRowType
> = (
    {
        productionId,
        title: name,
        date,
        day,
        time,
        href,
        className,
        children,
        venueName,
        location,
        isDesktop = false,
        ...props
    },
    ref: any,
) => {
    /* istanbul ignore next */
    const { pillLabels, title, matchups, isFinalFour, customDates } = useNCAAProperties(name, isDesktop)
    const classNames = classnames(styles.row, className, styles.withNCAARegionsFeature)

    return (
        <>
            <a {...props} ref={ref} className={styles.link} href={href}>
                <Row justify="left" align="stretch" className={classNames}>
                    <div className={styles.leftRail}>
                        <Text size="md" weight="semi-bold" className={!isDesktop ? styles.date : undefined}>
                            {customDates || date}
                        </Text>
                        <Column mt={0.125}>
                            <Text size="sm">
                                {day} • {time}
                            </Text>
                        </Column>
                    </div>
                    <Column
                        ml={!isDesktop ? 1 : undefined}
                        className={!isDesktop ? styles.titleWrapper : styles.desktopTitleWrapper}
                    >
                        {pillLabels.map((pillLabel) => (
                            <Pill
                                key={`${productionId}-${pillLabel}`}
                                className={classnames(styles.pill, { [styles.isFinalFour]: isFinalFour })}
                                importance="info"
                            >
                                {pillLabel}
                            </Pill>
                        ))}
                        <Text size="md" weight="semi-bold" className={!isDesktop ? styles.title : undefined}>
                            {title}
                        </Text>
                        <Column mb={0.0625}>
                            <Text size="sm" className={styles.label}>
                                {venueName} • {location}
                            </Text>
                        </Column>
                        {matchups && (
                            <Box display="flex" wrap className={styles.matchups}>
                                {matchups.map((matchup) => (
                                    <MatchupPill
                                        key={`${productionId}-${matchup[0][1]}`}
                                        team1={matchup[0]}
                                        team2={matchup[1]}
                                    />
                                ))}
                            </Box>
                        )}
                    </Column>
                    {children}
                    {!isDesktop && (
                        <Box alignSelf="center" marginLeft={5}>
                            <ArrowForwardIcon />
                        </Box>
                    )}
                </Row>
            </a>
        </>
    )
}

export default forwardRef(NcaaBasketballVariantProductionListingRow)
